import React, { useState } from 'react';
import { Button } from '../../components/Button/Button';
import { Card } from '../../components/Card/Card';
import { contact_info } from '../../constants/contact';
import './Contact.css';

interface ContactFormState {
  name: string;
  company: string;
  email: string;
  phone: string;
  message: string;
  referrer: string;
}

type ContactErrors = Partial<Record<keyof ContactFormState, string>>;

const referrer_sources = [
  'Google',
  'Search Engine',
  'LinkedIn',
  'YouTube',
  'GitHub',
  'Podcast',
  'Community',
  'Referral',
  'Event',
  'Other'
];

const initial_form_state: ContactFormState = {
  name: '',
  company: '',
  email: '',
  phone: '',
  message: '',
  referrer: ''
};

export const ContactModule: React.FC = () => {
  const [form_state, set_form_state] = useState<ContactFormState>(initial_form_state);
  const [status, set_status] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [errors, set_errors] = useState<ContactErrors>({});
  const [referrer_source, set_referrer_source] = useState('');

  const handle_referrer_select = (value: string): void => {
    set_referrer_source(value);
    if (value && value !== 'Other') {
      set_form_state((current_state) => ({ ...current_state, referrer: value }));
    }
    if (value === 'Other') {
      set_form_state((current_state) => ({ ...current_state, referrer: '' }));
    }
    if (errors.referrer) {
      set_errors((current_state) => {
        const next_errors = { ...current_state };
        delete next_errors.referrer;
        return next_errors;
      });
    }
  };

  const validate_field = (
    field: keyof ContactFormState,
    value: string
  ): ContactErrors => {
    const email_pattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phone_pattern = /^[+\d][\d\s().-]{6,}$/;
    const next_errors: ContactErrors = {};

    if (field === 'name' && !value.trim()) {
      next_errors.name = 'Name is required.';
    }

    if (field === 'company' && !value.trim()) {
      next_errors.company = 'Company is required.';
    }

    if (field === 'email') {
      if (!value.trim()) {
        next_errors.email = 'Email is required.';
      } else if (!email_pattern.test(value)) {
        next_errors.email = 'Enter a valid email.';
      }
    }

    if (field === 'phone' && value.trim() && !phone_pattern.test(value)) {
      next_errors.phone = 'Enter a valid phone number.';
    }

    if (field === 'message' && !value.trim()) {
      next_errors.message = 'Message is required.';
    }

    if (field === 'referrer' && !value.trim()) {
      next_errors.referrer = 'Referrer is required.';
    }

    return next_errors;
  };

  const validate_form = (payload: ContactFormState): ContactErrors => {
    const next_errors: ContactErrors = {};
    const email_pattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phone_pattern = /^[+\d][\d\s().-]{6,}$/;

    if (!payload.name.trim()) {
      next_errors.name = 'Name is required.';
    }

    if (!payload.company.trim()) {
      next_errors.company = 'Company is required.';
    }

    if (!payload.email.trim()) {
      next_errors.email = 'Email is required.';
    } else if (!email_pattern.test(payload.email)) {
      next_errors.email = 'Enter a valid email.';
    }

    if (payload.phone.trim() && !phone_pattern.test(payload.phone)) {
      next_errors.phone = 'Enter a valid phone number.';
    }

    if (!payload.message.trim()) {
      next_errors.message = 'Message is required.';
    }

    if (!payload.referrer.trim()) {
      next_errors.referrer = 'Referrer is required.';
    }

    return next_errors;
  };

  const handle_field_change = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ): void => {
    const { name, value } = event.target;
    set_form_state((current_state) => ({ ...current_state, [name]: value }));
    if (errors[name as keyof ContactFormState]) {
      const field_errors = validate_field(name as keyof ContactFormState, value);
      set_errors((current_state) => ({ ...current_state, ...field_errors }));
    }
  };

  const handle_field_blur = (
    event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
  ): void => {
    const { name, value } = event.target;
    const field_errors = validate_field(name as keyof ContactFormState, value);
    if (Object.keys(field_errors).length > 0) {
      set_errors((current_state) => ({ ...current_state, ...field_errors }));
      return;
    }
    set_errors((current_state) => {
      const next_errors = { ...current_state };
      delete next_errors[name as keyof ContactFormState];
      return next_errors;
    });
  };

  const handle_form_submit = async (
    event: React.FormEvent<HTMLFormElement>
  ): Promise<void> => {
    event.preventDefault();
    const next_errors = validate_form(form_state);

    if (Object.keys(next_errors).length > 0) {
      set_errors(next_errors);
      set_status('idle');
      return;
    }

    set_errors({});
    set_status('sending');

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form_state)
      });

      if (!response.ok) {
        throw new Error('Request failed');
      }

      set_status('success');
      set_referrer_source('');
      set_form_state({ ...initial_form_state, referrer: '' });
    } catch (error) {
      set_status('error');
    }
  };

  return (
    <section className="contact" id="contact">
      <div className="contact__container">
        <div className="contact__header">
          <p className="contact__eyebrow">Start the conversation</p>
          <h2 className="contact__title">Tell us what you need to host</h2>
          <p className="contact__subtitle">
            We will respond with availability, pricing guidance, and next steps inspired by
            the FancyWhale delivery standards.
          </p>
        </div>

        <div className="contact__content">
          <Card className="contact__card" variant="bordered" padding="large">
            <form className="contact__form" onSubmit={handle_form_submit}>
              <div className="contact__grid">
                <div className={`contact__field ${errors.name ? 'contact__field--error' : ''}`}
                >
                  <label htmlFor="name">Full name</label>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    autoComplete="name"
                    required
                    placeholder="Your name"
                    value={form_state.name}
                    onChange={handle_field_change}
                    onBlur={handle_field_blur}
                    className={errors.name ? 'contact__input--error' : undefined}
                    aria-invalid={Boolean(errors.name)}
                  />
                  {errors.name && <span className="contact__error">{errors.name}</span>}
                </div>

                <div className={`contact__field ${errors.company ? 'contact__field--error' : ''}`}
                >
                  <label htmlFor="company">Company</label>
                  <input
                    id="company"
                    name="company"
                    type="text"
                    autoComplete="organization"
                    required
                    placeholder="Company name"
                    value={form_state.company}
                    onChange={handle_field_change}
                    onBlur={handle_field_blur}
                    className={errors.company ? 'contact__input--error' : undefined}
                    aria-invalid={Boolean(errors.company)}
                  />
                  {errors.company && <span className="contact__error">{errors.company}</span>}
                </div>
              </div>

              <div className="contact__grid">
                <div className={`contact__field ${errors.email ? 'contact__field--error' : ''}`}
                >
                  <label htmlFor="email">Work email</label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    inputMode="email"
                    placeholder="you@company.com"
                    value={form_state.email}
                    onChange={handle_field_change}
                    onBlur={handle_field_blur}
                    className={errors.email ? 'contact__input--error' : undefined}
                    aria-invalid={Boolean(errors.email)}
                  />
                  <span className="contact__helper">We respond within one business day.</span>
                  {errors.email && <span className="contact__error">{errors.email}</span>}
                </div>

                <div className={`contact__field ${errors.phone ? 'contact__field--error' : ''}`}
                >
                  <label htmlFor="phone">Phone (optional)</label>
                  <input
                    id="phone"
                    name="phone"
                    type="tel"
                    autoComplete="tel"
                    inputMode="tel"
                    placeholder="+1 (555) 123-4567"
                    value={form_state.phone}
                    onChange={handle_field_change}
                    onBlur={handle_field_blur}
                    className={errors.phone ? 'contact__input--error' : undefined}
                    aria-invalid={Boolean(errors.phone)}
                  />
                  <span className="contact__helper">Add a direct line for faster follow-up.</span>
                  {errors.phone && <span className="contact__error">{errors.phone}</span>}
                </div>
              </div>

              <div className={`contact__field ${errors.message ? 'contact__field--error' : ''}`}
              >
                <label htmlFor="message">What are you looking to host?</label>
                <textarea
                  id="message"
                  name="message"
                  rows={5}
                  required
                  placeholder="Tell us what you need managed or hosted."
                  value={form_state.message}
                  onChange={handle_field_change}
                  onBlur={handle_field_blur}
                  className={errors.message ? 'contact__input--error' : undefined}
                  aria-invalid={Boolean(errors.message)}
                />
                {errors.message && <span className="contact__error">{errors.message}</span>}
              </div>

              <div className={`contact__field ${errors.referrer ? 'contact__field--error' : ''}`}
              >
                <label htmlFor="referrer">Referrer</label>
                <select
                  id="referrer"
                  name="referrer"
                  value={referrer_source}
                  onChange={(event) => handle_referrer_select(event.target.value)}
                  className={errors.referrer ? 'contact__input--error' : undefined}
                  aria-invalid={Boolean(errors.referrer)}
                  onBlur={() => {
                    if (referrer_source && referrer_source !== 'Other') {
                      set_errors((current_state) => {
                        const next_errors = { ...current_state };
                        delete next_errors.referrer;
                        return next_errors;
                      });
                    }
                  }}
                >
                  <option value="">Select a source</option>
                  {referrer_sources.map((source) => (
                    <option key={source} value={source}>
                      {source}
                    </option>
                  ))}
                </select>
                {referrer_source === 'Other' && (
                  <input
                    name="referrer"
                    type="text"
                    placeholder="Type your source"
                    value={form_state.referrer}
                    onChange={handle_field_change}
                    onBlur={handle_field_blur}
                    className={errors.referrer ? 'contact__input--error' : undefined}
                    aria-invalid={Boolean(errors.referrer)}
                  />
                )}
                {errors.referrer && <span className="contact__error">{errors.referrer}</span>}
              </div>

              <Button
                size="large"
                variant="primary"
                type="submit"
                fullWidth
                loading={status === 'sending'}
              >
                Send hosting request
              </Button>
              {status === 'success' && (
                <p className="contact__status">Request sent. We will follow up soon.</p>
              )}
              {status === 'error' && (
                <p className="contact__status contact__status--error">
                  Something went wrong. Please try again.
                </p>
              )}
            </form>
          </Card>

          <div className="contact__details">
            <div className="contact__detail">
              <span className="contact__detail-label">Email</span>
              <a href={`mailto:${contact_info.email}`}>{contact_info.email}</a>
            </div>
            <div className="contact__detail">
              <span className="contact__detail-label">Phone</span>
              <a href={`tel:${contact_info.phone_link}`}>{contact_info.phone_display}</a>
            </div>
            <div className="contact__detail">
              <span className="contact__detail-label">Location</span>
              <span>{contact_info.location}</span>
            </div>
            <div className="contact__note">
              <p>
                Prefer email? Reach us at {contact_info.email} for direct requests.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default {
  id: 'contact',
  name: 'Contact',
  component: ContactModule,
  priority: 3,
  render: () => <ContactModule />
};
